import React from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useIntl, defineMessages } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';

import { toggleDeviceSettings } from '../../../lib/actions/settings';
import { logEvent, Event } from '../../../lib/analytics';
import { State } from '../../../lib/reducers';
import { inMeeting } from '../../../lib/reduxSelectors/videoRoom';
import { IconSettings, IconSettingsSelected } from '../../IconSet';
import { FirstTimePopoverDialog } from '../../PopoverDialog';


const messages = defineMessages({
  mediaSettings: { id: 'mediaSettings' },
  mediaSettingsFirstTimeMsg: { id: 'mediaSettingsFirstTimeMsg' },
});


type MappedProps = {
  settingsOpen: boolean;
  inMeeting: boolean;
}

const mapStateToProps = (state: State): MappedProps => ({
  settingsOpen: state.settings.deviceSettingsDialogOpen,
  inMeeting: inMeeting(state)
});


function Icon({ settingsOpen }: { settingsOpen: boolean }) {
  return settingsOpen ?  <IconSettingsSelected size={32} /> : <IconSettings size={32} />;
}


function DesktopLayout({ settingsOpen, inMeeting }: MappedProps) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const openSettings = React.useCallback(
    () => {
      dispatch(toggleDeviceSettings());
      const from = inMeeting ? 'room' : 'home';
      logEvent(Event.MEDIA_SETTINGS, { 'from': from });
    }, [dispatch, inMeeting]
  );

  return (
    <Tooltip title={formatMessage(messages.mediaSettings)}>
      <IconButton onClick={openSettings}>
        <Icon settingsOpen={settingsOpen} />
      </IconButton>
    </Tooltip>
  );
}


const ConnectedDesktopLayout = connect(mapStateToProps)(DesktopLayout);


function MobileLayout({ settingsOpen }: MappedProps) {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const openSettings = React.useCallback(
    () => dispatch(toggleDeviceSettings())
    , [dispatch]
  );

  return (
    <MenuItem onClick={openSettings}>
      <ListItemIcon>
        <Icon settingsOpen={settingsOpen} />
      </ListItemIcon>
      <ListItemText primary={formatMessage(messages.mediaSettings)} />
    </MenuItem>
  );
}


const ConnectedMobileLayout = connect(mapStateToProps)(MobileLayout);


function MediaSettings(_props: {}) {
  const { formatMessage } = useIntl();

  if (isMobileOnly) {
    return <ConnectedMobileLayout />;
  }
  else {
    return (
      <FirstTimePopoverDialog
        configKey="mediaSettings"
        message={formatMessage(messages.mediaSettingsFirstTimeMsg)}
      >
        <ConnectedDesktopLayout />
      </FirstTimePopoverDialog>
    );
  }
}


export default MediaSettings;
